import getCollectionData from "../../utils/getCollectionData";
import getCollectionDataById from "../../utils/getCollectionDataById";
import getDocumentData from "../../utils/getDocumentData";
import runTransaction from "../../utils/runTransaction";

import articleStatuses from "../articles/constants/articleStatuses";
import articleTypes from "../articles/constants/articleTypes";
import updateArticle from "../articles/updateArticle";
import secondsToDuration from "../articles/utils/secondsToDuration";
import productionStatuses from "../productions/constants/productionStatuses";

import updateArtifact from "./updateArtifact";

export default async function deliverArtifacts(firebase, { publisher, productionId, user, article: selectedArticle }) {
	const db = firebase.firestore();

	const production = await getDocumentData(db.collection("productions").doc(productionId));

	const [work, articles, artifacts] = await Promise.all([
		getDocumentData(production.work.ref),
		getCollectionDataById(firebase, db.collection("articles"), production.articleIds),
		getCollectionData(
			db.collection("artifacts").where("production.id", "==", production.id).where("deleted", "==", false),
		),
	]);

	const deliveries = articles.flatMap((article) =>
		artifacts
			.filter((artifact) => production.articles?.[article.id]?.artifactIds?.includes(artifact.id))
			.map((artifact) => ({
				article,
				artifact,
			})),
	);

	// Deliver each artifact to the article and the work
	const articlesWithArtifact = await Promise.all(
		deliveries
			.filter(({ article }) => !selectedArticle || article.id === selectedArticle.id)
			.filter(({ artifact }) => !artifact.article)
			.map(async ({ article, artifact }) => {
				await updateArtifact(firebase, { ref: artifact.ref, work, article });

				await updateArticle(firebase, { ref: article.ref, artifact });

				return { article, artifact };
			}),
	);

	const artifactRefs = deliveries.map(({ artifact }) => artifact.ref);

	return runTransaction(firebase, [production.ref, artifactRefs], async (transaction, [production, artifacts]) => {
		const isComplete = articles.every((article) =>
			artifacts.some((artifact) => artifact.article?.id === article.id),
		);

		const readyArticles = articles.filter(
			(article) =>
				(!selectedArticle || article.id === selectedArticle.id) &&
				article.status === articleStatuses.READY &&
				publisher.automaticDistributionTypes.includes(article.type),
		);

		const exportableArticlesWithArtifact = articlesWithArtifact.filter(
			({ article, artifact }) =>
				[articleTypes.AUDIOBOOK, articleTypes.PODCAST].includes(article.type) && !!artifact,
		);

		if (isComplete) {
			const totalDuration = artifacts.reduce((acc, artifact) => acc + (artifact.duration || 0), 0);

			const data = {
				status: productionStatuses.DONE,
				deliveryDateActual: firebase.firestore.FieldValue.serverTimestamp(),
				deliveryDuration: secondsToDuration(totalDuration, { padHours: 2 }),
			};

			transaction.update(production.ref, data);

			transaction.set(production.ref.collection("events").doc(), {
				user: user.id,
				email: user.email,
				data,
				time: firebase.firestore.FieldValue.serverTimestamp(),
			});

			return {
				finalized: production.status !== productionStatuses.DONE,
				deliveryDuration: data.deliveryDuration,
				readyArticles,
				exportableArticlesWithArtifact,
			};
		}

		return {
			finalized: false,
			deliveryDuration: null,
			readyArticles,
			exportableArticlesWithArtifact,
		};
	});
}
